<template>
	<SectionCard title="Impostazioni del piano" icon="mdi-tune-variant">
		<div class="ss-summary">
			<div class="ss-fact">
				<v-icon small color="primary" class="mr-1">mdi-calendar-month-outline</v-icon><strong>{{ setting.times || 0 }}</strong
				>&nbsp;post al mese
			</div>
			<div class="ss-fact"><v-icon small color="primary" class="mr-1">mdi-share-variant-outline</v-icon>{{ nomiSocial || 'nessun social' }}</div>
			<div class="ss-fact"><v-icon small color="primary" class="mr-1">mdi-filter-outline</v-icon>{{ nomiFonti || 'nessuna fonte' }}</div>
			<div class="ss-fact"><v-icon small color="primary" class="mr-1">mdi-shape-outline</v-icon>{{ predefinito ? `template «${predefinito.name}»` : 'grafica di serie' }}</div>
			<v-spacer></v-spacer>
			<v-btn small text color="primary" data-tour="expand" @click="$emit('apri')">
				<v-icon left small>{{ aperto ? 'mdi-minus' : 'mdi-tune-variant' }}</v-icon
				>{{ aperto ? 'Chiudi impostazioni' : 'Impostazioni e template' }}
			</v-btn>
		</div>

		<div v-if="aperto" class="ss-body" data-tour="settings">
			<v-row>
				<v-col cols="12" sm="4">
					<v-text-field v-model.number="setting.times" label="Post ogni mese" type="number" min="0" max="62" prepend-inner-icon="mdi-calendar-month-outline" outlined dense hide-details />
				</v-col>
				<v-col cols="12" sm="4">
					<v-select v-model="setting.lang" :items="lingueScelta" label="Lingua dei post" prepend-inner-icon="mdi-translate" outlined dense hide-details />
				</v-col>
				<v-col cols="12" sm="4">
					<v-text-field
						v-model.number="setting.excludeDays"
						label="Non ripubblicare per (giorni)"
						type="number"
						min="0"
						prepend-inner-icon="mdi-repeat-off"
						outlined
						dense
						hide-details
						title="Un prodotto già pubblicato non torna nel piano prima di questi giorni"
					/>
				</v-col>
				<v-col cols="12" sm="6">
					<v-combobox
						v-model="setting.hours"
						:items="orariProposti"
						label="Orari di pubblicazione"
						prepend-inner-icon="mdi-clock-outline"
						hint="Si alternano tra un post e l'altro (formato 18:30)"
						persistent-hint
						multiple
						small-chips
						deletable-chips
						outlined
						dense
					/>
				</v-col>
				<v-col cols="12" sm="6">
					<div class="ss-label">Dove pubblicare</div>
					<div class="ss-socials">
						<button v-for="(s, key) in social" :key="key" type="button" class="ss-social" :class="{ 'ss-social--on': setting.platforms.includes(key) }" @click="cambiaSocial(key)">
							<v-icon small :color="setting.platforms.includes(key) ? s.colore : 'grey'" class="mr-1">{{ s.icona }}</v-icon
							>{{ s.label }}
						</button>
					</div>
				</v-col>
			</v-row>

			<div class="ss-label mt-4">Da dove prendere i prodotti</div>
			<div class="ss-sources" data-tour="sources">
				<div v-for="k in fonti" :key="k" class="ss-source">
					<v-switch :input-value="setting.sources[k] > 0" color="primary" hide-details class="mt-0 pt-0" :label="nomeFonte(k)" @change="setting.sources[k] = $event ? 1 : 0" />
					<div class="ss-help">{{ aiuto[k] }}</div>
				</div>
			</div>
			<div class="ss-help mt-1">Le fonti accese si alternano; un prodotto già pubblicato o programmato non viene ripreso. Se le fonti non bastano si pesca dal catalogo.</div>

			<div class="d-flex mt-3">
				<v-spacer></v-spacer>
				<v-btn depressed color="primary" @click="$emit('salva')"><v-icon left small>mdi-content-save-outline</v-icon>Salva impostazioni</v-btn>
			</div>

			<div class="ss-templates mt-4" data-tour="template-list">
				<div class="d-flex align-center">
					<div class="ss-label mb-0">Template salvati</div>
					<v-spacer></v-spacer>
					<v-btn small text color="primary" @click="$emit('nuovo-template')"><v-icon left small>mdi-plus</v-icon>Nuovo template</v-btn>
				</div>
				<div v-if="!templates.length" class="ss-help">Nessun template: la grafica si salva come template dalla scheda di un post o da «Nuovo template».</div>
				<div v-for="t in templates" :key="t._id" class="ss-template">
					<v-menu offset-y>
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon small v-bind="attrs" v-on="on" color="primary"><v-icon>mdi-dots-vertical</v-icon></v-btn>
						</template>
						<v-list dense>
							<v-list-item link @click="$emit('modifica-template', t)"><v-list-item-title class="size9">Modifica</v-list-item-title></v-list-item>
							<v-list-item v-if="!t.isDefault" link @click="$emit('predefinito', t)"><v-list-item-title class="size9">Rendi predefinito</v-list-item-title></v-list-item>
							<v-list-item link @click="$emit('elimina-template', t)"><v-list-item-title class="size9">Elimina</v-list-item-title></v-list-item>
						</v-list>
					</v-menu>
					<span class="ss-swatch">
						<span v-for="c in ['primary', 'secondary', 'accent']" :key="c" :style="{ background: colore(t, c) }"></span>
					</span>
					<span>{{ t.name }}</span>
					<span class="ss-help ml-2">{{ nomeLayout(t) }}</span>
					<v-chip v-if="t.isDefault" x-small label color="primary" class="ml-2">predefinito</v-chip>
				</div>
			</div>
		</div>
	</SectionCard>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import { SOCIAL, FONTI } from './formato';

// impostazioni del piano social e template salvati; il salvataggio resta nel genitore
export default {
	name: 'SocialSettings',
	components: { SectionCard },
	props: {
		setting: { type: Object, required: true },
		templates: { type: Array, default: () => [] },
		options: { type: Object, default: () => ({}) },
		lingue: { type: Array, default: () => [] },
		aperto: { type: Boolean, default: false },
	},
	data() {
		return {
			social: SOCIAL,
			fonti: ['viewed', 'sold', 'new', 'offer'],
			orariProposti: ['09:00', '10:00', '12:30', '13:00', '18:00', '18:30', '20:30', '21:00'],
			aiuto: {
				viewed: 'le schede più aperte sul sito negli ultimi 30 giorni',
				sold: 'i prodotti più ordinati negli ultimi 30 giorni',
				new: 'inseriti negli ultimi 30 giorni',
				offer: 'con prezzo barrato o dentro un’offerta attiva',
			},
		};
	},
	computed: {
		lingueScelta() {
			return [{ text: 'Lingua predefinita del sito', value: '' }, ...this.lingue.map(l => ({ text: l.toUpperCase(), value: l }))];
		},
		nomiSocial() {
			return (this.setting.platforms || [])
				.map(p => SOCIAL[p] && SOCIAL[p].label)
				.filter(Boolean)
				.join(', ');
		},
		nomiFonti() {
			return this.fonti
				.filter(k => this.setting.sources[k] > 0)
				.map(this.nomeFonte)
				.join(', ');
		},
		predefinito() {
			return this.templates.find(t => t.isDefault) || null;
		},
	},
	methods: {
		nomeFonte(k) {
			return FONTI[k].label;
		},
		cambiaSocial(key) {
			const list = this.setting.platforms || [];
			this.setting.platforms = list.includes(key) ? list.filter(p => p !== key) : [...list, key];
		},
		colore(t, c) {
			const preset = (this.options.presets || {})[t.design && t.design.occasion] || { theme: {} };
			return (t.design && t.design.theme && t.design.theme[c]) || preset.theme[c] || '#ccc';
		},
		nomeLayout(t) {
			const l = (this.options.layouts || []).find(x => x.key === (t.design && t.design.layout));
			return l ? l.label : '';
		},
	},
};
</script>

<style scoped>
.ss-summary {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px 18px;
}
.ss-fact {
	display: inline-flex;
	align-items: center;
	font-size: 13px;
	color: #1f2d3d;
}
.ss-body {
	border-top: 1px solid #eef1f5;
	margin-top: 12px;
	padding-top: 12px;
}
.ss-label {
	font-size: 13px;
	font-weight: 600;
	color: #1f2d3d;
	margin-bottom: 6px;
}
.ss-help {
	font-size: 12px;
	color: #5f6b7a;
}
.ss-socials {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}
.ss-social {
	display: inline-flex;
	align-items: center;
	padding: 5px 12px;
	border: 1px solid #dfe5ee;
	border-radius: 16px;
	background: #fff;
	font-size: 13px;
	cursor: pointer;
}
.ss-social--on {
	border-color: var(--v-primary-base);
	box-shadow: 0 0 0 1px var(--v-primary-base) inset;
}
.ss-sources {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
	gap: 10px 16px;
}
.ss-source .ss-help {
	margin-left: 52px;
	margin-top: -2px;
}
.ss-template {
	display: flex;
	align-items: center;
	font-size: 14px;
	padding: 2px 0;
}
.ss-template + .ss-template {
	border-top: 1px solid #eef1f5;
}
.ss-swatch {
	display: inline-flex;
	margin: 0 10px 0 4px;
}
.ss-swatch span {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	margin-right: -3px;
	border: 1px solid #fff;
}
</style>
